import React, { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerFreeConsultancyRequestTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { IFreeConsultancyRequest } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import { GETConsultancyRequests } from '../../services/consultancy';
import { ConsultancyRequests } from '../../components/ui/list/Consultancy';
import { useNavigate } from 'react-router-dom';
import AddConsultancyRequest from './AddConsultancyRequest';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const ManageFreeConsultancy = () => {
  const navigate = useNavigate();
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [requests, setRequests] = useState<IFreeConsultancyRequest[]>([]);
  const [totalRequests, setTotalRequest] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    fetchRequests(page, rowsPerPage, searchQuery, sort);
  }, [page, rowsPerPage, searchQuery, sort]);

  const fetchRequests = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETConsultancyRequests({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });

      // Ensure requests is always an array
      setRequests(response?.data || []);
      setTotalRequest(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
      setRequests([]);
      setTotalRequest(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refreshList = () => {
    fetchRequests(page, rowsPerPage, searchQuery, sort);
  };

  const handleSaveRefresh = () => {
    refreshList();
  };

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleSortRefresh = () => {
    setSort(sort === 'ASC' ? 'DESC' : 'ASC');
    refreshList();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Free Consultancy"
        createFilter={['SEARCH', 'ADD']}
        filterHeader={'Filter Technicians'}
        addbuttontitle="Add Request"
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onNavigate={() => setAddOpen(true)}
      />

      <AddConsultancyRequest
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleSaveRefresh}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {/* <CreateTableHeader tableCell={headerFreeConsultancyRequestTable} /> */}

        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* FIXED: Check if requests is defined AND has length */}
          {!isLoading && (!requests || requests.length === 0) ? (
            <Typography
              textAlign={'center'}
              marginTop={'20%'}
              color="textSecondary"
            >
              No Requests Found
            </Typography>
          ) : (
            <ConsultancyRequests
              requests={requests || []}
              isLoading={isLoading}
              onUpdate={refreshList}
              tableCell={headerFreeConsultancyRequestTable}
              onManageClick={(id: string) =>
                navigate(`/free-consultancy/request-details/${id}`)
              }
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0} mt={2}>
          <Pagination
            count={totalRequests}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(ManageFreeConsultancy);
